import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Bell, Mail, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { API, useApp } from "@/state/AppContext";
import { toast } from "sonner";
import usePush from "@/hooks/usePush";

export default function ActionsPage() {
  const navigate = useNavigate();
  const { currentResult, todoState, setTodoState, token, user, authHeaders } = useApp();
  const push = usePush();
  const [creatingReminder, setCreatingReminder] = useState(false);
  const [reminders, setReminders] = useState([]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/reminders`, { headers: authHeaders })
      .then(r => setReminders(r.data)).catch(() => {});
  }, [token, authHeaders]);

  if (!currentResult) {
    return <div className="p-6" data-testid="actions-empty">Keine Handlungsschritte verfügbar.</div>;
  }

  const steps = currentResult.todo_steps?.length ? currentResult.todo_steps : (
    currentResult.analysis?.required_action ? [currentResult.analysis.required_action] : []
  );

  const toggle = (i) => setTodoState({ ...todoState, [i]: !todoState[i] });

  const createReminder = async () => {
    const a = currentResult.analysis || {};
    if (!a.deadline) { toast.error("Kein Fristdatum erkannt."); return; }
    if (!user) { toast.info("Bitte anmelden, um Erinnerungen zu speichern."); navigate("/auth"); return; }
    setCreatingReminder(true);
    try {
      // Try to enable real web-push (silently continue on decline)
      if (push.supported && !push.subscribed) {
        try { await push.subscribe(); } catch (_) { void 0; }
      }
      const r = await axios.post(`${API}/reminders`, {
        document_id: currentResult.id,
        title: `${a.sender || "Brief"} – Frist`,
        reminder_date: a.deadline,
      }, { headers: authHeaders });
      setReminders([...reminders, r.data]);
      toast.success(push.subscribed
        ? "Erinnerung gespeichert. Wir senden dir am Fristtag eine Push-Benachrichtigung."
        : "Erinnerung gespeichert.");
    } catch (e) {
      toast.error("Erinnerung konnte nicht erstellt werden.");
    } finally { setCreatingReminder(false); }
  };

  return (
    <div className="px-5 pt-4 pb-8" data-testid="actions-page">
      <button onClick={() => navigate(-1)} data-testid="actions-back" className="text-slate-600 flex items-center gap-1 text-sm mb-3">
        <ArrowLeft className="w-4 h-4" /> Zurück
      </button>
      <h1 className="text-2xl font-bold text-slate-900">Deine nächsten Schritte</h1>
      <p className="text-slate-600 mt-1">Hake ab, was du erledigt hast.</p>

      <ul className="mt-6 space-y-3">
        {steps.length === 0 && <li className="text-slate-500 text-sm">Keine Aktion erforderlich.</li>}
        {steps.map((s, i) => {
          const done = !!todoState[i];
          return (
            <li key={i} className={`rounded-2xl p-4 border flex items-start gap-3 ${done ? "bg-emerald-50 border-emerald-100" : "bg-white border-slate-200"}`} data-testid={`todo-${i}`}>
              <Checkbox checked={done} onCheckedChange={() => toggle(i)} data-testid={`todo-cb-${i}`} className="mt-1" />
              <span className={`text-[15px] leading-relaxed ${done ? "line-through text-slate-500" : "text-slate-800"}`}>{s}</span>
            </li>
          );
        })}
      </ul>

      <div className="grid grid-cols-2 gap-3 mt-8">
        <button
          onClick={createReminder}
          disabled={creatingReminder}
          data-testid="reminder-btn"
          className="min-h-[54px] rounded-2xl bg-blue-50 text-blue-700 font-semibold flex items-center justify-center gap-2 border border-blue-100 disabled:opacity-60"
        >
          {creatingReminder ? <Loader2 className="w-5 h-5 animate-spin" /> : <Bell className="w-5 h-5" />}
          Erinnerung
        </button>
        <button
          onClick={() => navigate("/reply")}
          data-testid="actions-goto-reply"
          className="min-h-[54px] rounded-2xl bg-blue-600 text-white font-semibold flex items-center justify-center gap-2"
        >
          <Mail className="w-5 h-5" /> Antwort
        </button>
      </div>

      {reminders.length > 0 && (
        <div className="mt-6" data-testid="reminders-list">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Aktive Erinnerungen</div>
          <div className="space-y-2">
            {reminders.map(r => (
              <div key={r.id} className="rounded-xl bg-white border border-slate-200 p-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-900">{r.title}</div>
                  <div className="text-xs text-slate-500">{new Date(r.reminder_date).toLocaleDateString("de-DE")}</div>
                </div>
                <Bell className="w-4 h-4 text-blue-600" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
